/* /admin/audio-status — TTS cache + spend dashboard.
 *
 * Reads from GET /api/admin/audio-status (admin-only) and renders:
 *  - 30-day headline tiles: total calls, cache hit rate, characters billed
 *    to ElevenLabs, estimated GBP spend.
 *  - Per-language table: voice id, pronunciation quality flag, calls / hits
 *    / misses / chars billed / hit rate.
 *  - Disk cache summary: file count and total bytes.
 *
 * Used to:
 *  - Spot languages where the cache is under-warmed (low hit rate, high
 *    chars billed) so we can pre-warm the most-played words.
 *  - Keep an eye on ElevenLabs spend month-over-month.
 *  - Confirm the per-language pronunciation quality flag is accurate.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Volume2, Loader2, RefreshCw, ChevronLeft } from 'lucide-react';
import { api } from '@/lib/api';
import { useNoIndex } from '@/hooks/useSEO';

function formatBytes(n) {
    if (!n) return '0 B';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function formatChars(n) {
    if (!n) return '0';
    if (n < 1000) return String(n);
    if (n < 1_000_000) return `${(n / 1000).toFixed(1)}k`;
    return `${(n / 1_000_000).toFixed(2)}M`;
}

function QualityPill({ quality }) {
    const map = {
        native: 'bg-lq-success/10 text-lq-success border-lq-success/30',
        medium: 'bg-lq-peach-100 text-lq-peach-600 border-lq-peach-300',
        approximate: 'bg-lq-purple-50 text-lq-purple-700 border-lq-purple-300',
        unknown: 'bg-lq-ink-100 text-lq-ink-700 border-lq-ink-300',
    };
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-semibold capitalize ${map[quality] || map.unknown}`}>
            {quality || 'unknown'}
        </span>
    );
}

function Tile({ label, value, sub, testid }) {
    return (
        <div className="bg-white rounded-2xl border border-lq-ink-100 p-5" data-testid={testid}>
            <div className="text-xs uppercase tracking-wider font-semibold text-lq-ink-500">{label}</div>
            <div className="mt-2 font-display text-3xl text-lq-ink-900">{value}</div>
            {sub && <div className="mt-1 text-xs text-lq-ink-500">{sub}</div>}
        </div>
    );
}

export default function AdminAudioStatus() {
    useNoIndex('Audio status — admin');
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/admin/audio-status');
            setData(data);
            setErr(null);
        } catch (e) {
            setErr(e?.response?.data?.detail || 'Failed to load audio status');
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    if (loading && !data) {
        return (
            <main className="max-w-6xl mx-auto px-6 py-16 flex items-center gap-3 text-lq-ink-500">
                <Loader2 className="animate-spin" size={20} /> Loading audio status…
            </main>
        );
    }
    if (err) {
        return (
            <main className="max-w-2xl mx-auto px-6 py-16">
                <div className="bg-white border border-lq-error/30 rounded-2xl p-6">
                    <h1 className="font-display text-2xl font-semibold text-lq-error">{err}</h1>
                    <p className="mt-2 text-lq-ink-700">Are you signed in as an admin?</p>
                    <Link to="/admin" className="mt-4 inline-block text-lq-purple-700 hover:underline">Back to admin</Link>
                </div>
            </main>
        );
    }
    if (!data) return null;

    const u = data.usage_30d || {};
    const cache = data.cache || {};
    const hitRatePct = u.hit_rate !== null && u.hit_rate !== undefined ? `${(u.hit_rate * 100).toFixed(1)}%` : '—';

    return (
        <main className="max-w-6xl mx-auto px-6 py-10" data-testid="page-admin-audio-status">
            <div className="flex items-center justify-between gap-4">
                <div>
                    <Link to="/admin" className="text-sm text-lq-ink-500 hover:text-lq-purple-700 inline-flex items-center gap-1"><ChevronLeft size={14}/> Back to admin</Link>
                    <h1 className="mt-2 font-display text-3xl md:text-4xl font-semibold text-lq-ink-black inline-flex items-center gap-3">
                        <Volume2 size={28} className="text-lq-purple-500"/> Audio status
                    </h1>
                    <p className="mt-2 text-lq-ink-700">TTS cache health and ElevenLabs spend, last 30 days.</p>
                </div>
                <button onClick={load} className="inline-flex items-center gap-2 bg-white border-2 border-lq-ink-100 hover:border-lq-purple-300 text-lq-ink-900 font-semibold px-4 py-2 rounded-full text-sm" data-testid="audio-status-refresh">
                    <RefreshCw size={14}/> Refresh
                </button>
            </div>

            <section className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Tile label="Calls (30d)" value={u.calls?.toLocaleString() ?? '0'} sub={`${(u.hits ?? 0).toLocaleString()} hits · ${(u.misses ?? 0).toLocaleString()} misses`} testid="audio-tile-calls"/>
                <Tile label="Cache hit rate" value={hitRatePct} sub="Higher is better — every hit saves an ElevenLabs call" testid="audio-tile-hitrate"/>
                <Tile label="Characters billed" value={formatChars(u.chars_billed ?? 0)} sub="Sent to ElevenLabs (misses only)" testid="audio-tile-chars"/>
                <Tile label="Est. spend" value={`£${(u.estimated_cost_gbp ?? 0).toFixed(2)}`} sub="multilingual_v2 ≈ $0.30 / 1k chars @ £0.78/$" testid="audio-tile-spend"/>
            </section>

            <section className="mt-8 bg-white rounded-2xl border border-lq-ink-100 p-5">
                <h2 className="font-display text-lg font-semibold text-lq-ink-900">Per-language breakdown</h2>
                <div className="mt-4 overflow-x-auto">
                    <table className="w-full text-sm" data-testid="audio-language-table">
                        <thead>
                            <tr className="text-left text-xs uppercase tracking-wider text-lq-ink-500 border-b border-lq-ink-100">
                                <th className="py-2 pr-3">Language</th>
                                <th className="py-2 pr-3">Status</th>
                                <th className="py-2 pr-3">Voice</th>
                                <th className="py-2 pr-3">Quality</th>
                                <th className="py-2 pr-3 text-right">Calls</th>
                                <th className="py-2 pr-3 text-right">Hit rate</th>
                                <th className="py-2 pr-3 text-right">Chars billed</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(data.languages || []).map((l) => {
                                const x = l.usage_30d || { calls: 0, hits: 0, misses: 0, chars_billed: 0, hit_rate: null };
                                const hr = x.hit_rate !== null && x.hit_rate !== undefined ? `${(x.hit_rate * 100).toFixed(0)}%` : '—';
                                return (
                                    <tr key={l.slug} className="border-b border-lq-ink-100 last:border-0" data-testid={`audio-row-${l.slug}`}>
                                        <td className="py-2 pr-3 font-medium text-lq-ink-900">{l.name || l.slug}</td>
                                        <td className="py-2 pr-3 text-lq-ink-500">{l.status}</td>
                                        <td className="py-2 pr-3 font-mono text-xs text-lq-ink-500">{l.voice_id || '—'}</td>
                                        <td className="py-2 pr-3"><QualityPill quality={l.pronunciation_quality}/></td>
                                        <td className="py-2 pr-3 text-right">{x.calls.toLocaleString()}</td>
                                        <td className="py-2 pr-3 text-right">{hr}</td>
                                        <td className="py-2 pr-3 text-right">{formatChars(x.chars_billed)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </section>

            <section className="mt-8 bg-white rounded-2xl border border-lq-ink-100 p-5">
                <h2 className="font-display text-lg font-semibold text-lq-ink-900">On-disk cache</h2>
                <p className="mt-2 text-sm text-lq-ink-700">
                    {(cache.files ?? 0).toLocaleString()} cached MP3 files &middot; {formatBytes(cache.bytes ?? 0)} total.
                </p>
                <p className="mt-1 text-xs text-lq-ink-500">
                    Files live in <code>/app/frontend/public/audio-cache/</code> and are served by the backend at <code>/api/audio-cache/&lt;file&gt;.mp3</code>.
                </p>
            </section>
        </main>
    );
}
